import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';

const STATUS_CONFIG = {
  complete: { label: 'COMPLETE', dot: 'bg-white/30' },
  in_progress: { label: 'IN PROGRESS', dot: 'bg-white' },
  awaiting_confirmation: { label: 'AWAITING CONFIRMATION', dot: 'bg-amber-400' },
  executing: { label: 'EXECUTING', dot: 'bg-white' },
  cancelled: { label: 'CANCELLED', dot: 'bg-white/20' },
  error: { label: 'ERROR', dot: 'bg-red-400' },
};

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

/**
 * AgentSessionList — left rail session history.
 */
const AgentSessionList = ({ onNewSession, onSessionSelect }) => {
  const { sessions, activeSession, loadSessions, loadSession, deleteSession } = useAgent();
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleDelete = async (e, sessionId) => {
    e.stopPropagation();
    await deleteSession(sessionId);
  };

  return (
    <div className="flex flex-col h-full bg-accent">
      <div className="px-3 py-3 border-b border-white/20">
        <button
          onClick={onNewSession}
          className="w-full text-xs font-medium text-accent bg-white border border-white/20 rounded px-3 py-1.5 hover:bg-blue-50 transition-colors"
        >
          + New session
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 && (
          <p className="text-xs text-white/40 text-center px-4 py-6">No sessions yet</p>
        )}
        {sessions.map((session) => {
          const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.complete;
          const isActive = activeSession?.id === session.id;
          return (
            <div
              key={session.id}
              className={`relative group border-b border-white/10 transition-colors ${
                isActive ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
              onMouseEnter={() => setHoveredId(session.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <button
                onClick={() => { loadSession(session.id); onSessionSelect?.(); }}
                className="w-full text-left px-3 py-3 pr-8"
              >
                <p className="text-xs font-medium text-white truncate">
                  {session.title || 'Untitled session'}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <span className="text-xs text-white/60 font-medium">{cfg.label}</span>
                  <span className="text-xs text-white/40">· {relativeTime(session.created_at)}</span>
                </div>
                <p className="text-xs text-white/40 mt-0.5">
                  {session.tool_call_count > 0
                    ? `${session.tool_call_count} action${session.tool_call_count !== 1 ? 's' : ''}`
                    : `${session.message_count} message${session.message_count !== 1 ? 's' : ''}`}
                </p>
              </button>

              {hoveredId === session.id && (
                <button
                  onClick={(e) => handleDelete(e, session.id)}
                  className="absolute top-2.5 right-2 p-0.5 rounded text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                  title="Delete session"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AgentSessionList;
